import ButtonComponent from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/styles'
import PropTypes from 'prop-types'
import React from 'react'

import { colors } from 'app/theme/colors'
import { radius, space } from 'app/theme/tokens'

/**
 * Shared button styles. `button` is the legacy/default appearance (yellow,
 * lowercase, bold) that existing callers depend on — DO NOT change its shape
 * since many pages still import this hook directly:
 *   `import { useStyles } from 'app/components/generic/Button'`
 *
 * New intent-based variants live alongside it. Use them via the `intent` prop
 * on <Button>, not by reaching for `classes.<variantName>` from outside.
 */
export const useStyles = makeStyles((theme) => ({
  button: {
    backgroundColor: theme.palette.background.secondary,
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    textTransform: 'lowercase'
  },
  primary: {
    backgroundColor: colors.ohOrange,
    color: colors.white,
    fontWeight: 600,
    borderRadius: radius.pill,
    padding: `${space.sm} ${space.lg}`,
    textTransform: 'lowercase',
    '&:hover': { backgroundColor: colors.ohDarkOrange }
  },
  secondary: {
    backgroundColor: 'transparent',
    color: colors.ohOrange,
    border: `1.5px solid ${colors.ohOrange}`,
    fontWeight: 600,
    borderRadius: radius.pill,
    padding: `${space.sm} ${space.lg}`,
    textTransform: 'lowercase',
    '&:hover': { backgroundColor: colors.ohLightOrange }
  },
  danger: {
    backgroundColor: colors.ohRed,
    color: colors.white,
    fontWeight: 600,
    borderRadius: radius.pill,
    padding: `${space.sm} ${space.lg}`,
    textTransform: 'lowercase',
    '&:hover': { backgroundColor: colors.ohDarkOrange }
  },
  yellow: {
    backgroundColor: colors.ohYellow,
    color: colors.textPrimary,
    fontWeight: 600,
    borderRadius: radius.md,
    padding: `${space.sm} ${space.md}`,
    textTransform: 'lowercase',
    '&:hover': { backgroundColor: '#ddb31c' }
  }
}))

const Button = ({ intent, className, ...props }) => {
  const classes = useStyles()
  const intentClass = intent ? classes[intent] : classes.button
  const finalClassName = className
    ? `${intentClass} ${className}`
    : intentClass

  return (
    <ButtonComponent
      variant="contained"
      className={finalClassName}
      {...props}
    />
  )
}

Button.propTypes = {
  intent: PropTypes.oneOf(['primary', 'secondary', 'danger', 'yellow']),
  className: PropTypes.string
}

export default Button
